import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IEditor, EditContext, RectProps } from '@visactor/vtable-editors';

export interface MultiSelectEditorOptions {
  options: string[];
  placeholder?: string;
  maxHeight?: number;
  searchable?: boolean;
  isSingleSelect?: boolean; // 新增：是否为单选模式
}

export class MultiSelectEditor implements IEditor {
  private element: HTMLDivElement | null = null;
  private container: HTMLElement | null = null;
  private successCallback: Function | null = null;
  private currentValue: string[] = [];
  private editorConfig: MultiSelectEditorOptions;
  private cellRect: RectProps | null = null; // 新增：保存单元格位置信息

  constructor(editorConfig: MultiSelectEditorOptions) {
    this.editorConfig = {
      placeholder: '请选择...',
      maxHeight: 200,
      searchable: true,
      isSingleSelect: false, // 默认为多选模式
      ...editorConfig
    };
  }

  onStart({ container, value, referencePosition, endEdit }: EditContext) {
    this.container = container;
    this.successCallback = endEdit;
    this.currentValue = Array.isArray(value) ? [...value] : [];
    this.cellRect = referencePosition?.rect || null; // 保存单元格位置

    this._createEditor();
    
    if (this.cellRect) {
      this.adjustPosition(this.cellRect);
    }
  }

  private _createEditor() {
    this.element = document.createElement('div');
    this.element.className = 'vtable-multiselect-editor';
    
    const handleClose = (save: boolean) => {
      if (save && this.successCallback) {
        this.successCallback(this.currentValue);
      } else if (this.successCallback) {
        this.successCallback(false);
      }
    };

    const handleChange = (newValue: string[]) => {
      this.currentValue = newValue;
    };

    ReactDOM.render(
      <MultiSelectDropdown
        options={this.editorConfig.options}
        value={this.currentValue}
        onChange={handleChange}
        onClose={handleClose}
        placeholder={this.editorConfig.placeholder}
        maxHeight={this.editorConfig.maxHeight}
        searchable={this.editorConfig.searchable}
        isSingleSelect={this.editorConfig.isSingleSelect} // 传递单选模式配置
        cellWidth={this.cellRect?.width || 250} // 新增：传递单元格宽度
      />,
      this.element
    );

    this.container?.appendChild(this.element);
  }

  adjustPosition(rect: RectProps) {
    // if (this.element) {
    //   let top = rect.top + rect.height + 2; // 单元格下方，留2px间隙
    //   this.element.style.position = 'absolute';
    //   this.element.style.top = `${top}px`;
    //   this.element.style.left = `${rect.left}px`;
    //   this.element.style.zIndex = '1000';
    // }

    if (!this.element) return;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 233; // 估算下拉框最大高度
    
    // 计算基本位置（单元格下方）
    let top = rect.top + rect.height + 2; // 单元格下方，留2px间隙
    let left = rect.left;
    
    // 边界检测：如果下拉框超出屏幕底部，向上展开
    if (280 + top + dropdownHeight > viewportHeight) {
      top = rect.top - dropdownHeight - 2; // 单元格上方
      
      // 如果向上也不够空间，则使用可用空间
      if (top < 0) {
        top = Math.max(10, rect.top + rect.height + 2);
      }
    }
    
    // 边界检测：如果下拉框超出屏幕右侧，向左调整
    const dropdownWidth = Math.max(rect.width, 200); // 使用单元格宽度或最小宽度
    if (left + dropdownWidth > viewportWidth) {
      left = Math.max(10, viewportWidth - dropdownWidth - 10);
    }

    // 应用位置和宽度
    this.element.style.position = 'absolute'; // 使用fixed定位，相对于视窗
    this.element.style.top = `${top}px`;
    this.element.style.left = `${left}px`;
    this.element.style.width = `${Math.max(rect.width, 200)}px`; // 最小宽度200px
    this.element.style.zIndex = '1000';
    
    console.log('下拉框定位信息:', {
      单元格位置: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      下拉框位置: { top, left, width: Math.max(rect.width, 200) },
      视窗大小: { width: viewportWidth, height: viewportHeight, dropdownHeight: dropdownHeight },
    });
  }

  getValue() {
    return this.currentValue;
  }

  onEnd() {
    if (this.element && this.container?.contains(this.element)) {
      ReactDOM.unmountComponentAtNode(this.element);
      this.container.removeChild(this.element);
    }
    this.element = null;
    this.container = null;
    this.successCallback = null;
  }

  isEditorElement(target: HTMLElement): boolean {
    return this.element ? this.element.contains(target) : false;
  }
}

interface MultiSelectDropdownProps {
  options: string[];
  value: string[];
  onChange: (newValue: string[]) => void;
  onClose: (save: boolean) => void;
  placeholder?: string;
  maxHeight?: number;
  searchable?: boolean;
  isSingleSelect?: boolean; // 新增：是否为单选模式
  cellWidth?: number; // 新增：单元格宽度
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  onClose,
  placeholder = '请选择...',
  maxHeight = 200,
  isSingleSelect = false, // 新增参数
  searchable = true,
  
  cellWidth = 250, // 新增参数
}) => {
  const [selected, setSelected] = useState<string[]>([...value]);
  const [searchText, setSearchText] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<string[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 搜索过滤
  useEffect(() => {
    if (!searchable || !searchText.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchText, options, searchable]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose(false);
      } else if (event.key === 'Enter' && event.ctrlKey) {
        handleSave();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    // 自动聚焦到搜索框
    if (searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose, searchable]);

  const handleOptionClick = (option: string) => {
    let newSelected: string[];
    // if (selected.includes(option)) {
    //   newSelected = selected.filter(item => item !== option);
    // } else {
    //   newSelected = [...selected, option];
    // }
    // setSelected(newSelected);
    // onChange(newSelected);
    if (isSingleSelect) {
      // 单选模式：只能选择一个选项
      newSelected = selected.includes(option) ? [] : [option];
      setSelected(newSelected);
      onChange(newSelected);
      
      // 单选模式下选择选项后自动保存并关闭
      if (newSelected.length > 0) {
        setTimeout(() => {
          onClose(true);
        }, 150); // 延迟150ms让用户看到选择效果
      }
    } else {
      // 多选模式：可以选择多个选项
      if (selected.includes(option)) {
        newSelected = selected.filter(item => item !== option);
      } else {
        newSelected = [...selected, option];
      }
      setSelected(newSelected);
      onChange(newSelected);
    }
  };

  const handleSelectAll = () => {
    // 单选模式下不显示全选按钮，这个函数不会被调用
    if (isSingleSelect) return;
    const allSelected = filteredOptions.every(option => selected.includes(option));
    let newSelected: string[];
    
    if (allSelected) {
      // 取消选中所有过滤选项
      newSelected = selected.filter(item => !filteredOptions.includes(item));
    } else {
      // 选中所有过滤选项
      const newItems = filteredOptions.filter(option => !selected.includes(option));
      newSelected = [...selected, ...newItems];
    }
    
    setSelected(newSelected);
    onChange(newSelected);
  };

  const handleSave = () => {
    onClose(true);
  };

  const handleCancel = () => {
    onClose(false);
  };

  const selectedCount = selected.length;
  const filteredSelectedCount = filteredOptions.filter(option => selected.includes(option)).length;
  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(option => selected.includes(option));

  // 计算下拉框宽度
  const dropdownWidth = Math.max(cellWidth, 100); // 最小200px，确保可用性

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        background: '#fff',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        // minWidth: '250px',
        // maxWidth: '350px',
        width: `${dropdownWidth}px`, // 使用动态宽度
        zIndex: 1000,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* 头部 */}
      <div style={{
        padding: '12px',
        borderBottom: '1px solid #f0f0f0',
        background: '#fafafa',
        borderRadius: '6px 6px 0 0',
      }}>
        {searchable && (
          <div style={{ marginBottom: '8px' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="搜索选项..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          color: '#666',
        }}>
          <span>
            {isSingleSelect 
              ? (selectedCount > 0 ? '已选择 1 项' : '请选择一项') 
              : `已选择 ${selectedCount} 项`
            }
          </span>
          {/* 单选模式下不显示全选按钮 */}
          {!isSingleSelect && filteredOptions.length > 0 && (
            <button
              onClick={handleSelectAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#1890ff',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '0',
              }}
            >
              {allFilteredSelected ? '取消全选' : '全选'}
            </button>
          )}
        </div>
      </div>

      {/* 选项列表 */}
      <div style={{
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
        padding: '4px 0',
      }}>
        {filteredOptions.length === 0 ? (
          <div style={{
            padding: '12px',
            textAlign: 'center',
            color: '#999',
            fontSize: '14px',
          }}>
            {searchText ? '无匹配选项' : '暂无选项'}
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = selected.includes(option);
            return (
              <div
                key={option}
                onClick={() => handleOptionClick(option)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                  color: isSelected ? '#1890ff' : '#333',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.target as HTMLElement).style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: `2px solid ${isSelected ? '#1890ff' : '#d9d9d9'}`,
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected ? '#1890ff' : 'transparent',
                  transition: 'all 0.2s',
                }}>
                  {isSelected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span style={{ flex: 1 }}>{option}</span>
              </div>
            );
          })
        )}
      </div>

      {/* 底部按钮 */}
      {/* <div style={{
        padding: '12px',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
      }}>
        <button
          onClick={handleCancel}
          style={{
            flex: 1,
            padding: '6px 16px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            background: '#fff',
            color: '#666',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLElement).style.borderColor = '#40a9ff';
            (e.target as HTMLElement).style.color = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.borderColor = '#d9d9d9';
            (e.target as HTMLElement).style.color = '#666';
          }}
        >
          取消
        </button>
        <button
          onClick={handleSave}
          style={{
            flex: 1,
            padding: '6px 16px',
            border: '1px solid #1890ff',
            borderRadius: '4px',
            background: '#1890ff',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLElement).style.backgroundColor = '#40a9ff';
            (e.target as HTMLElement).style.borderColor = '#40a9ff';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.backgroundColor = '#1890ff';
            (e.target as HTMLElement).style.borderColor = '#1890ff';
          }}
        >
          确定 ({selectedCount})
        </button>
      </div> */}
    </div>
  );
};